import React, { useEffect, useRef, useState } from "react";
import MainLayout from "../sidebar/Sidebar.js";
import api from "../api/api";

// This page intentionally uses the device's native camera UI by leveraging
// a hidden <input type="file" accept="image/*" capture="environment" />.
// On mobile browsers, this opens the native camera. We also show a simple
// in-app permission prompt before invoking the OS-level permission dialog.

export default function CameraTest2() {
  const fileInputRef = useRef(null);
  const [capturedImageUrl, setCapturedImageUrl] = useState(null);
  const [capturedFile, setCapturedFile] = useState(null);
  const [errorMessage, setErrorMessage] = useState("");
  const [isRequesting, setIsRequesting] = useState(false);
  const [isPermissionDialogOpen, setIsPermissionDialogOpen] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState("");
  const [uploadResult, setUploadResult] = useState(null);
  const nativePickerOpenRef = useRef(false);
  const pickerCleanupRef = useRef(null);
  const [toast, setToast] = useState(null); // { type: 'success' | 'error', message }
  const toastTimeoutRef = useRef(null);
  const [imageDims, setImageDims] = useState({ width: null, height: null });

  const isSecureContext = () => {
    const isLocalhost =
      typeof window !== "undefined" &&
      (window.location.hostname === "localhost" ||
        window.location.hostname === "127.0.0.1" ||
        window.location.hostname === "[::1]");
    return (
      isLocalhost ||
      (typeof window !== "undefined" && window.location.protocol === "https:")
    );
  };

  useEffect(() => {
    return () => {
      if (capturedImageUrl) {
        URL.revokeObjectURL(capturedImageUrl);
      }
      if (pickerCleanupRef.current) {
        pickerCleanupRef.current();
        pickerCleanupRef.current = null;
      }
      if (toastTimeoutRef.current) {
        clearTimeout(toastTimeoutRef.current);
        toastTimeoutRef.current = null;
      }
    };
  }, [capturedImageUrl]);

  const handleOpenPermissionDialog = () => {
    setErrorMessage("");
    setIsPermissionDialogOpen(true);
  };

  const attachNativePickerReturnHandlers = () => {
    const finalizeIfReturned = () => {
      if (!nativePickerOpenRef.current) return;
      // Defer to allow input state to settle
      setTimeout(() => {
        const hasFile = !!(
          fileInputRef.current &&
          fileInputRef.current.files &&
          fileInputRef.current.files.length > 0
        );
        if (!hasFile) {
          // Treat as cancel: soft reset UI without reloading
          if (fileInputRef.current) {
            fileInputRef.current.value = "";
          }
          setIsRequesting(false);
          nativePickerOpenRef.current = false;
          if (pickerCleanupRef.current) {
            pickerCleanupRef.current();
            pickerCleanupRef.current = null;
          }
          return;
        }
        nativePickerOpenRef.current = false;
        if (pickerCleanupRef.current) {
          pickerCleanupRef.current();
          pickerCleanupRef.current = null;
        }
      }, 0);
    };

    const onFocus = () => finalizeIfReturned();
    const onVisibilityChange = () => {
      if (document.visibilityState === "visible") finalizeIfReturned();
    };
    const onPageShow = () => finalizeIfReturned();
    const onPointerDown = () => finalizeIfReturned();

    window.addEventListener("focus", onFocus);
    document.addEventListener("visibilitychange", onVisibilityChange);
    window.addEventListener("pageshow", onPageShow);
    window.addEventListener("pointerdown", onPointerDown, { once: true });

    return () => {
      window.removeEventListener("focus", onFocus);
      document.removeEventListener("visibilitychange", onVisibilityChange);
      window.removeEventListener("pageshow", onPageShow);
      window.removeEventListener("pointerdown", onPointerDown);
    };
  };

  const openNativeCamera = () => {
    setIsPermissionDialogOpen(false);
    setIsRequesting(true);
    try {
      if (!fileInputRef.current) {
        throw new Error("Camera input is not ready");
      }
      nativePickerOpenRef.current = true;
      // Install temporary listeners to detect return from native picker
      pickerCleanupRef.current = attachNativePickerReturnHandlers();
      // Optional: some browsers fire a non-standard 'cancel' event
      const inputEl = fileInputRef.current;
      const onCancel = () => {
        // Soft cancel: reset UI state and cleanup without reloading
        if (fileInputRef.current) {
          fileInputRef.current.value = "";
        }
        setIsRequesting(false);
        nativePickerOpenRef.current = false;
        if (pickerCleanupRef.current) {
          pickerCleanupRef.current();
          pickerCleanupRef.current = null;
        }
      };
      inputEl.addEventListener &&
        inputEl.addEventListener("cancel", onCancel, { once: true });
      const prevCleanup = pickerCleanupRef.current;
      pickerCleanupRef.current = () => {
        prevCleanup && prevCleanup();
        inputEl.removeEventListener &&
          inputEl.removeEventListener("cancel", onCancel);
      };
      // Trigger the native camera via file input. Must be in a user gesture.
      fileInputRef.current.click();
    } catch (err) {
      console.error(err);
      setErrorMessage(err?.message || "Unable to open camera");
      setIsRequesting(false);
      nativePickerOpenRef.current = false;
      if (pickerCleanupRef.current) {
        pickerCleanupRef.current();
        pickerCleanupRef.current = null;
      }
    }
  };

  const handlePermissionAllow = () => {
    openNativeCamera();
  };

  const handlePermissionCancel = () => {
    setIsPermissionDialogOpen(false);
  };

  const handleFileChange = (event) => {
    try {
      const file = event.target.files && event.target.files[0];
      if (!file) {
        setIsRequesting(false);
        return;
      }

      if (capturedImageUrl) {
        URL.revokeObjectURL(capturedImageUrl);
      }
      const objectUrl = URL.createObjectURL(file);
      setCapturedImageUrl(objectUrl);
      setCapturedFile(file);
      setUploadResult(null);
      setUploadError("");

      // Probe image dimensions for finalize step
      const img = new Image();
      img.onload = () => {
        const w = Number(img.naturalWidth) || null;
        const h = Number(img.naturalHeight) || null;
        setImageDims({ width: w, height: h });
      };
      img.onerror = () => setImageDims({ width: null, height: null });
      img.src = objectUrl;
    } catch (err) {
      console.error(err);
      setErrorMessage("Failed to capture image");
    } finally {
      // Reset input so selecting the same capture again still triggers change
      event.target.value = "";
      setIsRequesting(false);
      nativePickerOpenRef.current = false;
      if (pickerCleanupRef.current) {
        pickerCleanupRef.current();
        pickerCleanupRef.current = null;
      }
    }
  };

  const handleRetake = () => {
    setErrorMessage("");
    openNativeCamera();
  };

  const clearImage = () => {
    if (capturedImageUrl) URL.revokeObjectURL(capturedImageUrl);
    setCapturedImageUrl(null);
    setCapturedFile(null);
    setUploadResult(null);
    setUploadError("");
  };

  const handleUpload = async () => {
    try {
      setUploadError("");
      if (!capturedFile) {
        setUploadError("No image to upload");
        return;
      }
      setUploading(true);
      // 1) Ask backend to start a direct-to-S3 upload
      const startResp = await api.post("/photos/start-upload");
      const { photo_id, original_key, post_url, post_fields } =
        startResp.data || {};
      if (!post_url || !post_fields || !photo_id) {
        throw new Error("Failed to start upload");
      }

      // 2) Build S3 form data with provided fields, set Content-Type, append file
      const s3Form = new FormData();
      Object.entries(post_fields).forEach(([k, v]) => s3Form.append(k, v));
      const contentType =
        capturedFile.type && capturedFile.type.startsWith("image/")
          ? capturedFile.type
          : "image/jpeg";
      s3Form.append("Content-Type", contentType);
      s3Form.append("file", capturedFile);

      // iOS Safari can throw TypeError: Load failed even when S3 accepted the upload (esp. with 204 responses)
      // We configure presigned POST for 201 responses, but still tolerate network errors by finalizing anyway.
      let s3NetworkError = false;
      try {
        const s3Resp = await fetch(post_url, { method: "POST", body: s3Form });
        if (!s3Resp.ok) {
          s3NetworkError = true;
        }
      } catch (e) {
        s3NetworkError = true;
      }

      if (s3NetworkError) {
        console.warn("S3 upload may have failed, backend will verify.");
      }

      // 3) Finalize with backend so the record shows as uploaded
      const dims = imageDims || {};
      await api.post(`/photos/${photo_id}/complete`, {
        width: Number(dims.width) || undefined,
        height: Number(dims.height) || undefined,
      });

      // Optionally fetch detail for a preview URL
      let detailData = null;
      try {
        const detailResp = await api.get(`/photos/${photo_id}`);
        detailData = detailResp.data;
      } catch (e) {
        // Non-fatal
      }

      setUploadResult({
        photo_id,
        keys: { original: original_key },
        original_url: detailData?.original_url || null,
      });
      // Success toast
      if (toastTimeoutRef.current) clearTimeout(toastTimeoutRef.current);
      setToast({ type: "success", message: "Photo uploaded" });
      toastTimeoutRef.current = setTimeout(() => setToast(null), 2500);
    } catch (err) {
      console.error(err);
      setUploadError(
        err?.response?.data?.detail ||
          err?.message ||
          "Failed to upload photo to gallery"
      );
      if (toastTimeoutRef.current) clearTimeout(toastTimeoutRef.current);
      setToast({ type: "error", message: "Upload failed" });
      toastTimeoutRef.current = setTimeout(() => setToast(null), 3000);
    } finally {
      setUploading(false);
    }
  };

  return (
    <MainLayout location="Camera Test 2" setLocation={() => {}}>
      <div className="bg-gray-50 min-h-full flex flex-col py-4 px-6">
        <div className="w-full bg-white border-b p-4 flex items-center justify-between">
          <h1 className="text-xl font-semibold text-gray-900">Camera Test 2</h1>
        </div>

        {/* Toast */}
        {toast && (
          <div className={`fixed top-4 inset-x-0 flex justify-center z-50`}>
            <div
              className={`px-4 py-2 rounded shadow text-white text-sm ${
                toast.type === "success" ? "bg-green-600" : "bg-red-600"
              }`}
            >
              {toast.message}
            </div>
          </div>
        )}

        {!isSecureContext() && (
          <div className="m-4 p-3 rounded border border-yellow-300 bg-yellow-50 text-yellow-800 text-sm">
            Camera access requires HTTPS in production. Localhost is okay for
            development.
          </div>
        )}

        <div className="flex-1 p-4 space-y-6">
          <div className="flex justify-center">
            <button
              onClick={handleOpenPermissionDialog}
              disabled={isRequesting}
              className="px-6 py-3 rounded-xl bg-blue-600 text-white text-base font-medium shadow-md hover:bg-blue-700 active:scale-[0.99] disabled:opacity-50"
            >
              {isRequesting ? "Opening..." : "Capture Image"}
            </button>
          </div>

          <div>
            <h2 className="text-lg font-medium text-gray-900 mb-2">
              Captured Photo
            </h2>
            <div className="aspect-video bg-white border rounded-xl flex items-center justify-center overflow-hidden">
              {capturedImageUrl ? (
                <img
                  src={capturedImageUrl}
                  alt="Captured"
                  className="w-full h-full object-contain"
                />
              ) : (
                <p className="text-gray-500">No photo captured yet.</p>
              )}
            </div>
            {capturedImageUrl && (
              <div className="mt-3 flex gap-3">
                <button
                  onClick={handleRetake}
                  className="px-4 py-2 bg-gray-100 text-gray-800 rounded-lg hover:bg-gray-200"
                >
                  Retake
                </button>
                <button
                  onClick={clearImage}
                  className="px-4 py-2 bg-white text-gray-700 border rounded-lg hover:bg-gray-50"
                >
                  Clear
                </button>
                <button
                  onClick={handleUpload}
                  disabled={uploading}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
                >
                  {uploading ? "Saving..." : "Save to Gallery"}
                </button>
              </div>
            )}
          </div>

          {errorMessage && (
            <div className="p-3 rounded border border-red-300 bg-red-50 text-red-800 text-sm">
              {errorMessage}
            </div>
          )}

          {uploadError && (
            <div className="p-3 rounded border border-red-300 bg-red-50 text-red-800 text-sm">
              {uploadError}
            </div>
          )}

          {uploadResult && (
            <div className="p-3 rounded border border-green-300 bg-green-50 text-green-800 text-sm break-words">
              <div className="font-medium mb-1">Uploaded</div>
              <div>photo_id: {uploadResult.photo_id}</div>
              <div className="mt-2">
                <div className="font-medium">Keys</div>
                <div className="text-xs text-gray-700">
                  original: {uploadResult.keys?.original}
                </div>
                <div className="text-xs text-gray-700">
                  medium: {uploadResult.keys?.medium}
                </div>
                <div className="text-xs text-gray-700">
                  thumbnail: {uploadResult.keys?.thumbnail}
                </div>
              </div>
              {uploadResult.original_url && (
                <div className="mt-2">
                  <a
                    href={uploadResult.original_url}
                    target="_blank"
                    rel="noreferrer"
                    className="text-blue-700 underline"
                  >
                    View original
                  </a>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Hidden native camera input */}
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          capture="environment"
          onChange={handleFileChange}
          style={{ display: "none" }}
        />

        {/* Simple in-app permission dialog */}
        {isPermissionDialogOpen && (
          <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4">
            <div className="w-full max-w-md rounded-xl bg-white shadow-lg p-6">
              <h3 className="text-lg font-semibold text-gray-900">
                Allow Camera Access
              </h3>
              <p className="mt-2 text-sm text-gray-600">
                This app would like to use your camera to capture a photo. Your
                device may show a permission prompt next.
              </p>
              <div className="mt-4 flex items-center justify-end gap-3">
                <button
                  onClick={handlePermissionCancel}
                  className="px-4 py-2 rounded-lg bg-gray-100 text-gray-800 hover:bg-gray-200"
                >
                  Cancel
                </button>
                <button
                  onClick={handlePermissionAllow}
                  className="px-4 py-2 rounded-lg bg-blue-600 text-white hover:bg-blue-700"
                >
                  Allow
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </MainLayout>
  );
}
